import { sendSearchPageVideoClick } from '@/analytics';
import { SendSearchPageVideoClickParams } from '@/analytics/types';
import { MagicVariables } from '@/types';
import { getCountry, getSexualOrientation } from '@/utils/general';

export const MAX_ITEM_POSITION = 100;
export const MAX_PAGE_NUMBER = 4;

export class TrackVideoClick implements MagicVariables {
    $root: MagicVariables['$root'];

    private videoItems: HTMLElement[];
    private isLoggedIn: boolean;
    private searchKeyword: string;

    constructor(isLoggedIn: boolean, searchKeyword: string) {
        this.isLoggedIn = isLoggedIn;
        this.searchKeyword = searchKeyword;
    }

    init($root: MagicVariables['$root']) {
        this.$root = $root;
        this.getVideoItems();
        this.setupVideoItemClickListener();
    }

    setupVideoItemClickListener() {
        this.videoItems.forEach((videoItem) => {
            videoItem.addEventListener('click', this.onVideoItemClick.bind(this));
        });
    }

    onVideoItemClick(event: MouseEvent) {
        const videoItem = event.currentTarget as HTMLElement;
        const trackingData = this.getTrackingData(videoItem);

        if (!this.isTrackingDataValid(trackingData)) {
            return;
        }

        this.sendGaEvent(trackingData);
    }

    sendGaEvent(data: SendSearchPageVideoClickParams) {
        sendSearchPageVideoClick(data);
    }

    getTrackingData(videoItem: HTMLElement): SendSearchPageVideoClickParams {
        const itemPosition = this._getVideoItemPosition(videoItem);
        const videoId = this._getVideoItemId(videoItem);
        const pageNumber = this._getCurrentPageNumber(this.$root);

        const countryCode = getCountry();
        const orientation = getSexualOrientation();

        return {
            videoId,
            pageNumber,
            itemPosition,
            countryCode: countryCode,
            orientation: orientation,
            keyword: this.searchKeyword,
            loggedIn: this.isLoggedIn,
        };
    }

    isTrackingDataValid(trackingData: SendSearchPageVideoClickParams): boolean {
        if (
            trackingData.videoId < 0 ||
            trackingData.pageNumber < 1 ||
            trackingData.pageNumber > MAX_PAGE_NUMBER ||
            trackingData.itemPosition < 1 ||
            trackingData.itemPosition > MAX_ITEM_POSITION
        ) {
            return false;
        }

        if (!trackingData.keyword || !trackingData.countryCode || !trackingData.orientation) {
            return false;
        }

        return true;
    }

    getVideoItems(): void {
        this.videoItems = Array.from(this.$root.querySelectorAll('.video-item'));

        // There are 2 search page variations currently, and video items can be selected differently.
        if (!this.videoItems.length) {
            this.videoItems = Array.from(this.$root.querySelectorAll('[data-testid="video-item"]'));
        }
    }

    /**
     * Returns the position of the video item, starting from 1.
     *
     * @returns {number} - The position of the video item in the list.
     */
    private _getVideoItemPosition(videoItem: HTMLElement): number {
        return Array.from(this.videoItems).indexOf(videoItem) + 1;
    }

    /**
     * Returns the data-id attribute value of the video item element.
     *
     * @param {HTMLElement} videoItem - The video item element.
     * @returns {number} - The video ID as a number.
     */
    private _getVideoItemId(videoItem: HTMLElement): number {
        const videoId = videoItem.getAttribute('data-id');
        return Number(videoId);
    }

    /**
     * Returns the current page number from the root element's data attribute.
     *
     * @returns {number} - -1 if the page number is invalid.
     */
    private _getCurrentPageNumber(rootEl: HTMLElement): number {
        const page = rootEl.getAttribute('data-page');

        try {
            const pageNumber = parseInt(page, 10);

            if (pageNumber < 1) {
                return -1;
            }

            return pageNumber;
        } catch (error) {
            return -1;
        }
    }
}
